// Helpers for the "a new app version is available" check.
//
// Everything here is defensive: the app must keep working normally if the
// version API is down, returns something unexpected, or the Capacitor plugins
// are unavailable (e.g. running as a plain website).

// Only these hosts may be opened by the Update button. downloadUrl comes from
// the database, so without this check a wrong or tampered row could send users
// to a phishing page or a "javascript:" URL.
const ALLOWED_STORE_HOSTS = [
  "apps.apple.com",
  "itunes.apple.com",
  "play.google.com",
];

const FALLBACK_STORE_URL = {
  ios: "https://apps.apple.com/in/app/zpotter/id6740035107",
  android:
    "https://play.google.com/store/apps/details?id=in.zpotter.www",
};

/**
 * Numeric, segment-wise version compare — "1.2.10" is newer than "1.2.9",
 * which a plain string comparison gets wrong.
 * Returns 1 (a > b), -1 (a < b), 0 (equal), or null when either side is not
 * a usable version string.
 */
export const compareVersions = (a, b) => {
  const parse = (value) =>
    String(value ?? "")
      .trim()
      // tolerate a leading "v" and any "-beta" style suffix
      .replace(/^v/i, "")
      .split("-")[0]
      .split(".")
      .map((part) => Number.parseInt(part, 10));

  const left = parse(a);
  const right = parse(b);

  if (!left.length || !right.length) return null;
  if (left.some(Number.isNaN) || right.some(Number.isNaN)) return null;

  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const l = left[i] ?? 0;
    const r = right[i] ?? 0;
    if (l > r) return 1;
    if (l < r) return -1;
  }
  return 0;
};

/** True only for an https link to a real app store. */
export const isAllowedStoreUrl = (value) => {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" && ALLOWED_STORE_HOSTS.includes(url.hostname)
    );
  } catch {
    return false;
  }
};

/**
 * The store URL to send the user to: the one from the API when it is a genuine
 * store link, otherwise our own known-good link for that platform.
 */
export const resolveStoreUrl = (downloadUrl, platform) =>
  isAllowedStoreUrl(downloadUrl)
    ? downloadUrl
    : FALLBACK_STORE_URL[platform] || null;

/**
 * The API returns every release row for the platform, so pick the highest
 * version rather than trusting document order.
 */
export const pickLatestRelease = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return rows.reduce((best, row) => {
    if (!best) return row;
    return compareVersions(row?.version, best?.version) === 1 ? row : best;
  }, null);
};

const CHECK_KEY = "gymAdminAppVersionCheck";

/** Local date as YYYY-MM-DD, so the check runs once per calendar day. */
export const todayKey = () => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

/**
 * The whole result is cached, not just the date. Without this, killing and
 * reopening the app on the same day would hide a pending update, because the
 * once-a-day rule blocks the API call and there would be nothing to show.
 *
 * Shape: { checkedOn, latestVersion, message, storeUrl }
 */
export const readCheck = () => {
  try {
    const raw = localStorage.getItem(CHECK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    // Storage blocked or corrupt JSON — behave as if nothing was cached.
    return null;
  }
};

export const writeCheck = (result) => {
  try {
    localStorage.setItem(CHECK_KEY, JSON.stringify(result));
  } catch {
    // Worst case the check simply runs again on the next open.
  }
};

export const wasCheckedToday = () => readCheck()?.checkedOn === todayKey();
